import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDocumentVisible } from './useDocumentVisible';

const AVERAGE_TIME: number = 10;
const IS_ON_FPS: boolean = true;

export const usePerformance = () => {
    const isBrowserActive = useDocumentVisible();
    const isFpsRunning = useMemo<boolean>(
        () => process.env.NODE_ENV === 'development' && IS_ON_FPS,
        []
    );
    const [fps, setFps] = useState<number>(0);
    const [averageFPS, setAverageFPS] = useState<number>();

    const frame = useRef<number>();
    const frameCount = useRef<number>(0);
    const averageSum = useRef<number>(0);
    const averageCount = useRef<number>(0);
    const last = useRef<number>(0);

    const animate = useCallback(() => {
        if (!isFpsRunning) return;

        frameCount.current++;
        const now = performance.now();
        const delta = (now - last.current) / 1000;
        if (delta >= 1) {
            const fps: number = Math.floor(frameCount.current / delta); // Calculate FPS
            frameCount.current = 0;
            last.current = now;
            setFps(fps);

            averageSum.current += fps;
            averageCount.current++;
            if (averageCount.current >= AVERAGE_TIME) {
                const tmp: number = Math.floor(
                    averageSum.current / averageCount.current
                ); // Calculate Rate
                averageCount.current = 0;
                averageSum.current = 0;
                setAverageFPS(tmp);
            }
        }
        frame.current = requestAnimationFrame(animate);
    }, [isFpsRunning]);

    useEffect(() => {
        if (isBrowserActive && isFpsRunning) {
            //reset
            last.current = performance.now();
            frameCount.current = 0;
            averageCount.current = 0;
            averageSum.current = 0;
        }
    }, [isBrowserActive, isFpsRunning]);

    useEffect(() => {
        if (isFpsRunning) {
            last.current = performance.now();
            frame.current = requestAnimationFrame(animate);
        }

        return () => {
            frame.current && cancelAnimationFrame(frame.current);
        };
    }, [isFpsRunning, animate]);

    return { fps, isFpsRunning, averageFPS };
};
